// Evidence resolvers for the data-driven optimization card renderer.
//
// Each resolver reads the v2 contract field first and falls back to the legacy
// `evidence.*` shape, normalizing BOTH into one row shape so the UI (EntityTable
// columns, charts) can stay static. Pure functions — no React, no side effects.
//
// v2 fields:   peer_comparison, adset_overlap_pairs, audience_expansion,
//              daily_series, metrics_extended, metric_keys_to_show,
//              account_benchmarks, value_change_display, value_unit, items.
// legacy:      evidence.peer_ads, evidence.auction_overlap_pairs,
//              evidence.audience_segments, evidence.cpa_streak/roas_streak,
//              evidence.analysis_window.

import {
  buildCpaStreakFromDailySeries,
  buildRoasStreakFromDailySeries,
} from 'components/optimizations/subcomponents/streakUtils';

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const nonEmptyArray = (v) => (Array.isArray(v) && v.length > 0 ? v : null);

export const toNum = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// ── metrics / context ────────────────────────────────────────────────────────

export const resolveMetricsExtended = (s) => (isObj(s?.metrics_extended) ? s.metrics_extended : null);

export const resolveAccountBenchmarks = (s) => (isObj(s?.account_benchmarks) ? s.account_benchmarks : null);

export const resolveAnalysisWindow = (s) => {
  const aw = s?.evidence?.analysis_window;
  return isObj(aw) ? aw : null;
};

// Mirror of the modal's resolveMetricKeysToShow (kept in sync; the modal owns an
// identical private copy that cannot be imported without coupling to it).
export const resolveMetricKeysToShow = (opt, criteria, fallbackBase) => {
  const fromBackend = Array.isArray(opt?.metric_keys_to_show) ? opt.metric_keys_to_show : null;
  if (fromBackend) return fromBackend.slice(0, 8);
  const base = fallbackBase || ['spend', 'conversions', 'cpa', 'roas'];
  const fromCriteria = (criteria || [])
    .map((c) => c?.metric_key)
    .filter((k) => k && !base.includes(k));
  return [...base, ...fromCriteria];
};

// Mirror of the modal's resolveVisibleItems: drop dismissed items and any item
// that duplicates the suggestion's primary_entity (already shown in the header).
export const resolveVisibleItems = (opt) => {
  const items = Array.isArray(opt?.items) ? opt.items : [];
  const primaryExtId = opt?.primary_entity?.external_id != null ? String(opt.primary_entity.external_id) : null;
  const primaryId = opt?.primary_entity?.id != null ? String(opt.primary_entity.id) : null;
  return items.filter((it) => {
    if (it?.is_liked === false) return false;
    const extId = it?.target_entity?.external_id != null ? String(it.target_entity.external_id) : null;
    const id = it?.target_entity?.id != null ? String(it.target_entity.id) : null;
    if (primaryExtId && extId && extId === primaryExtId) return false;
    if (primaryId && id && id === primaryId) return false;
    return true;
  });
};

// Selectable rows for the action footer / picker: every non-dismissed item,
// INCLUDING the one that matches primary_entity. resolveVisibleItems hides the
// primary because it's shown in the entity header; the picker instead lists every
// candidate entity (the header ad included) so each can be toggled for /apply.
export const resolveSelectableItems = (opt) => {
  const items = Array.isArray(opt?.items) ? opt.items : [];
  return items.filter((it) => it?.is_liked !== false);
};

// ── currency ─────────────────────────────────────────────────────────────────
// The preview renderer has no auth context, so it cannot use the user's active
// Meta/Google currency; it resolves from the payload only.
const SENTINEL_UNITS = new Set(['none', 'ratio', 'seconds', '']);

export const resolveCurrency = (s) => {
  const me = resolveMetricsExtended(s);
  if (me && typeof me.currency === 'string' && me.currency.trim()) return me.currency.trim();
  const vcd = s?.value_change_display;
  if (isObj(vcd) && typeof vcd.currency === 'string' && vcd.currency.trim()) return vcd.currency.trim();
  const unit = typeof s?.value_unit === 'string' ? s.value_unit.trim() : '';
  if (unit && !SENTINEL_UNITS.has(unit.toLowerCase())) return unit;
  return 'USD';
};

// ── value change (D directly; value-modifying B/C via top-level value_before/after) ──
// `value_change_display` is emitted only for archetype D (contract §5). For other
// archetypes that still change a value (e.g. a B "Reducir presupuesto"), the amount
// travels in the compat fields value_before / value_after / value_unit — synthesize
// a value_change_display-shaped object from them so the before→after can render.
const VALUE_FIELD_LABELS = [
  [/budget/, 'Presupuesto diario'],
  [/roas/, 'Target ROAS'],
  [/cpa/, 'Target CPA'],
  [/cost_cap/, 'Cost cap'],
  [/bid/, 'Puja'],
];

const synthesizeValueChange = (s) => {
  const before = toNum(s?.value_before);
  const after = toNum(s?.value_after);
  if (before == null || after == null) return null;
  const unit = typeof s?.value_unit === 'string' ? s.value_unit.trim() : '';
  const actionType = String(s?.action_type || '').toLowerCase();
  const isRatio = unit.toLowerCase() === 'ratio' || actionType.includes('roas');
  const me = resolveMetricsExtended(s);
  const currency = isRatio
    ? null
    : (unit && !SENTINEL_UNITS.has(unit.toLowerCase()))
      ? unit
      : (me && typeof me.currency === 'string' && me.currency.trim() ? me.currency.trim() : null);
  const match = VALUE_FIELD_LABELS.find(([re]) => re.test(actionType));
  const deltaAbsolute = after - before;
  return {
    field_label: match ? match[1] : 'Valor',
    value_before: before,
    value_after: after,
    delta_absolute: deltaAbsolute,
    delta_percent: before !== 0 ? (deltaAbsolute / before) * 100 : 0,
    currency,
    direction: after > before ? 'increase' : after < before ? 'decrease' : 'replace',
    format_hint: isRatio ? 'ratio' : (currency ? 'currency_decimal' : 'decimal_2'),
  };
};

export const resolveValueChange = (s) => {
  if (isObj(s?.value_change_display)) return s.value_change_display;
  return synthesizeValueChange(s);
};

// ── peer comparison (archetypes A/E) ─────────────────────────────────────────
// v2 peer_comparison ‖ legacy evidence.peer_ads → { adName, isCurrent, isWinner, roas, cpa, spend, conversions }
export const resolvePeerComparison = (s) => {
  const v2 = nonEmptyArray(s?.peer_comparison);
  if (v2) {
    return v2.map((p) => ({
      adName: p?.ad_name ?? null,
      isCurrent: Boolean(p?.is_current),
      isWinner: Boolean(p?.is_winner),
      roas: toNum(p?.roas),
      cpa: toNum(p?.cpa),
      spend: toNum(p?.spend),
      conversions: toNum(p?.conversions),
    }));
  }
  const legacy = nonEmptyArray(s?.evidence?.peer_ads);
  if (legacy) {
    const lastIdx = legacy.length - 1;
    return legacy.map((p, idx) => {
      const name = p?.ad_name ?? null;
      // Legacy lacks is_current/is_winner; infer "current" via the modal's heuristic.
      const isCurrent = (typeof name === 'string' && name.toLowerCase().includes('este anuncio')) || idx === lastIdx;
      return {
        adName: name,
        isCurrent,
        isWinner: false,
        roas: toNum(p?.roas),
        cpa: toNum(p?.cpa),
        spend: toNum(p?.spend),
        conversions: toNum(p?.conversions),
      };
    });
  }
  return null;
};

// ── adset overlap (archetype H) ──────────────────────────────────────────────
// v2 adset_overlap_pairs ‖ legacy evidence.auction_overlap_pairs.
// v2 lacks spend/roas (→ null); columns adapt to whichever fields are present.
export const resolveAdsetOverlapPairs = (s) => {
  const src = nonEmptyArray(s?.adset_overlap_pairs) || nonEmptyArray(s?.evidence?.auction_overlap_pairs);
  if (!src) return null;
  return src.map((p) => ({
    adsetAName: p?.adset_a_name ?? null,
    adsetBName: p?.adset_b_name ?? null,
    overlapPct: toNum(p?.overlap_pct),
    spendA: toNum(p?.spend_a),
    spendB: toNum(p?.spend_b),
    roasA: toNum(p?.roas_a),
    roasB: toNum(p?.roas_b),
  }));
};

// ── audience expansion (archetype H) ─────────────────────────────────────────
// v2 audience_expansion ‖ legacy evidence.audience_segments.
export const resolveAudienceExpansion = (s) => {
  const v2 = nonEmptyArray(s?.audience_expansion);
  if (v2) {
    return v2.map((a) => ({
      label: a?.label ?? null,
      note: null,
      audienceSize: toNum(a?.audience_size),
      isCurrent: Boolean(a?.is_current),
      spend: toNum(a?.spend),
      conversions: toNum(a?.conversions),
      cpa: toNum(a?.cpa),
    }));
  }
  const legacy = nonEmptyArray(s?.evidence?.audience_segments);
  if (legacy) {
    return legacy.map((a) => ({
      label: a?.segment_label ?? null,
      note: a?.note ?? null,
      audienceSize: toNum(a?.audience_size),
      isCurrent: false,
      spend: toNum(a?.spend_7d),
      conversions: toNum(a?.conversions_7d),
      cpa: toNum(a?.cpa_7d),
    }));
  }
  return null;
};

// ── daily streak (archetypes B/D/E) ──────────────────────────────────────────
// v2 daily_series (cpa|roas) via streakUtils ‖ legacy evidence.cpa_streak/roas_streak.
// Returns { type: 'cpa'|'roas', streak } or null.
export const resolveDailyStreak = (s) => {
  const ds = s?.daily_series;
  if (isObj(ds)) {
    if (ds.metric_key === 'cpa') {
      const streak = buildCpaStreakFromDailySeries(ds);
      if (streak) return { type: 'cpa', streak };
    }
    if (ds.metric_key === 'roas') {
      const streak = buildRoasStreakFromDailySeries(ds);
      if (streak) return { type: 'roas', streak };
    }
  }
  if (isObj(s?.evidence?.cpa_streak)) return { type: 'cpa', streak: s.evidence.cpa_streak };
  if (isObj(s?.evidence?.roas_streak)) return { type: 'roas', streak: s.evidence.roas_streak };
  return null;
};

// ── single bundle ────────────────────────────────────────────────────────────
// Everything the card blocks read, resolved once from the raw suggestion.
export const resolveSuggestionEvidence = (s) => {
  const criteria = Array.isArray(s?.criteria) ? s.criteria : [];
  return {
    criteria,
    metricsExtended: resolveMetricsExtended(s),
    metricKeysToShow: resolveMetricKeysToShow(s, criteria),
    accountBenchmarks: resolveAccountBenchmarks(s),
    analysisWindow: resolveAnalysisWindow(s),
    visibleItems: resolveVisibleItems(s),
    windowComparison: isObj(s?.window_comparison) ? s.window_comparison : null,
    valueChangeDisplay: resolveValueChange(s),
    diagnosticScore: isObj(s?.diagnostic_score) ? s.diagnostic_score : null,
    structuralChange: isObj(s?.structural_change) ? s.structural_change : null,
    subItems: Array.isArray(s?.sub_items) ? s.sub_items : null,
    subItemsSummary: isObj(s?.sub_items_summary) ? s.sub_items_summary : null,
    peerComparison: resolvePeerComparison(s),
    adsetOverlap: resolveAdsetOverlapPairs(s),
    audienceExpansion: resolveAudienceExpansion(s),
    dailyStreak: resolveDailyStreak(s),
  };
};
